import {
  Box,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import { MoreVert, ContentCopy } from "@mui/icons-material";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import moment from "moment";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { addRecentProject } from "../utils";
import DEFEKT_TRACKER from "@/app/permissionNew/defectTracker";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { setSelectedItem } from "@/features/bughurd/slice";
import CardPagination from "@/Components/Common/CardPagination";

type ProjectCardViewProps = {
  projects: any;
  isLoading: boolean;
  isFetching: boolean;
  boardTypeFilter: string;
  paginationModel: {
    pageSize: number;
    page: number;
  };
  setPaginationModel: (model: any) => void;
};

export default function ProjectCardView({
  projects,
  isLoading,
  isFetching,
  boardTypeFilter,
  paginationModel,
  setPaginationModel,
}: ProjectCardViewProps) {
  const { roleCodeDetails: myPermissions, dateFormat } = useAppSelector(
    (state) => state.appGlobal,
  );
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation(["bugherd"]);
  const baseUrl = window?.location?.origin;
  const isPmoHub = baseUrl?.includes("pmohub") || false;
  const [cardMenuAnchor, setCardMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [selectedProject, setSelectedProject] = useState<any>(null);

  const handleCardMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    project: any,
  ) => {
    event.stopPropagation();
    setCardMenuAnchor(event.currentTarget);
    setSelectedProject(project);
  };

  const handleCardMenuClose = () => {
    setCardMenuAnchor(null);
    setSelectedProject(null);
  };

  const handleCopyProjectId = () => {
    if (selectedProject?._id) {
      navigator.clipboard.writeText(selectedProject._id);
      toast.success(t("Project ID copied to clipboard"));
    }
    handleCardMenuClose();
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 380px)",
          minHeight: "300px",
        }}
      >
        {isLoading || isFetching ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flex={1}
          >
            <CircularProgress />
          </Box>
        ) : (projects?.items || [])?.length === 0 ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flex={1}
          >
            <Typography>{t("No Data Found")}</Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                minHeight: 0,
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(1, 1fr)",
                    sm: "repeat(2, 1fr)",
                    lg: "repeat(3, 1fr)",
                  },
                  gap: "20px",
                  pb: 2,
                }}
              >
                {(projects?.items || [])
                  ?.filter((project: any) => {
                    // Filter by board type
                    if (boardTypeFilter === "all") return true;
                    return project?.boardType === boardTypeFilter;
                  })
                  ?.map((project: any) => {
                    const projectUrl = `/defect-tracker/projects/${project._id}/${project.boardType === "SCRUM" ? "backlog" : "task-board"}`;

                    const handleCardClick = () => {
                      if (!DEFEKT_TRACKER.VIEW(myPermissions)) return;
                      addRecentProject(project);
                      dispatch(setSelectedItem("Dashboard"));
                      navigate(projectUrl);
                    };

                    return (
                      <Box
                        key={project._id}
                        onClick={handleCardClick}
                        sx={{
                          background: (theme) =>
                            theme.palette.mode === "dark"
                              ? "rgba(255, 255, 255, 0.05)"
                              : "rgba(0, 0, 0, 0.02)",
                          backdropFilter: "blur(20px)",
                          border: (theme) =>
                            `1px solid ${
                              theme.palette.mode === "dark"
                                ? "rgba(255, 255, 255, 0.1)"
                                : "rgba(0, 0, 0, 0.1)"
                            }`,
                          borderRadius: "20px",
                          p: 3,
                          cursor: DEFEKT_TRACKER.VIEW(myPermissions)
                            ? "pointer"
                            : "default",
                          transition:
                            "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                          position: "relative",
                          overflow: "hidden",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: "3px",
                            background: project?.icon || "#4DABF7",
                            opacity: 0,
                            transition: "opacity 0.3s ease",
                          },
                          "&:hover": DEFEKT_TRACKER.VIEW(myPermissions)
                            ? {
                                transform: "translateY(-2px)",
                                background: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "rgba(255, 255, 255, 0.08)"
                                    : "rgba(0, 0, 0, 0.04)",
                                boxShadow: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "0 25px 50px rgba(0, 0, 0, 0.4)"
                                    : "0 25px 50px rgba(0, 0, 0, 0.15)",
                                borderColor: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? `${theme.palette.primary.main}50`
                                    : `${theme.palette.primary.main}30`,
                                "&::before": {
                                  opacity: 1,
                                },
                                "& .card-actions": {
                                  opacity: 1,
                                },
                              }
                            : {},
                        }}
                      >
                        {/* Actions Button */}
                        <Box
                          className="card-actions"
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            opacity: 0,
                            transition: "opacity 0.2s ease",
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={(e) => handleCardMenuOpen(e, project)}
                          >
                            <MoreVert sx={{ fontSize: 20 }} />
                          </IconButton>
                        </Box>

                        {/* Project Icon with First Character */}
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={2}
                          mb={2.5}
                        >
                          <Box
                            sx={{
                              width: 56,
                              height: 56,
                              borderRadius: "16px",
                              backgroundColor: (theme) =>
                                project?.icon || theme.palette.primary.main,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "22px",
                              fontWeight: 700,
                              color: (theme) => theme.palette.common.white,
                              flexShrink: 0,
                              boxShadow: (theme) =>
                                `0 8px 20px ${project?.icon || theme.palette.primary.main}40`,
                            }}
                          >
                            {project?.name?.charAt(0)?.toUpperCase() || "P"}
                          </Box>
                          <Box
                            flex={1}
                            minWidth={0}
                            display="flex"
                            flexDirection="column"
                            justifyContent="center"
                          >
                            <Typography
                              variant="h6"
                              fontWeight={600}
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                fontSize: "18px",
                                color: (theme) => theme.palette.text.primary,
                                mb: 0.75,
                                lineHeight: 1.2,
                              }}
                            >
                              {project?.name}
                            </Typography>
                            <Box
                              component="span"
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                background: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "rgba(255, 255, 255, 0.1)"
                                    : "rgba(0, 0, 0, 0.08)",
                                px: 1.25,
                                py: 0.5,
                                borderRadius: "6px",
                                fontSize: "12px",
                                fontWeight: 600,
                                color: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "rgba(255, 255, 255, 0.7)"
                                    : "rgba(0, 0, 0, 0.6)",
                                fontFamily: "'Monaco', 'Menlo', monospace",
                                letterSpacing: "0.5px",
                                alignSelf: "flex-start",
                              }}
                            >
                              {project?.projectKey}
                            </Box>
                            {project?.configurationType && (
                              <Box
                                component="span"
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  background: (theme) =>
                                    `${theme.palette.primary.main}15`,
                                  px: 1.25,
                                  py: 0.5,
                                  borderRadius: "6px",
                                  fontSize: "12px",
                                  fontWeight: 600,
                                  color: (theme) => theme.palette.primary.main,
                                  alignSelf: "flex-start",
                                }}
                              >
                                {project.configurationType === "SUPPORT"
                                  ? "Support"
                                  : project.configurationType}
                              </Box>
                            )}
                          </Box>
                        </Box>

                        {/* Created By, Created Date */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 1,
                            pt: 1,
                            px: 0.5,
                            borderTop: (theme) =>
                              `1px solid ${
                                theme.palette.mode === "dark"
                                  ? "rgba(255, 255, 255, 0.06)"
                                  : "rgba(0, 0, 0, 0.06)"
                              }`,
                          }}
                        >
                          {/* Created By */}
                          <Box display="flex" alignItems="center" gap={0.75}>
                            <PersonOutlineIcon
                              sx={{
                                fontSize: 15,
                                color: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "rgba(255, 255, 255, 0.4)"
                                    : "rgba(0, 0, 0, 0.35)",
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: "12px",
                                color: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "rgba(255, 255, 255, 0.5)"
                                    : "rgba(0, 0, 0, 0.45)",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {project?.createdBy?.fullName || "-"}
                            </Typography>
                          </Box>

                          {/* Created Date */}
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <CalendarTodayIcon
                              sx={{
                                fontSize: 13,
                                color: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "rgba(255, 255, 255, 0.4)"
                                    : "rgba(0, 0, 0, 0.35)",
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: "11px",
                                color: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "rgba(255, 255, 255, 0.5)"
                                    : "rgba(0, 0, 0, 0.45)",
                              }}
                            >
                              {project?.createdAt
                                ? moment(project.createdAt).format(
                                    dateFormat || "MMM D YYYY",
                                  )
                                : "-"}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
              </Box>
            </Box>

            {/* Pagination - stuck at bottom */}
            <Box sx={{ flexShrink: 0, pt: 1 }}>
              <CardPagination
                currentPage={paginationModel.page}
                totalItems={projects?.totalCount || 0}
                pageSize={paginationModel.pageSize}
                onPageChange={(page) =>
                  setPaginationModel((prev: any) => ({ ...prev, page }))
                }
              />
            </Box>
          </>
        )}
      </Box>

      {/* Menu for Card Actions */}
      <Menu
        anchorEl={cardMenuAnchor}
        open={Boolean(cardMenuAnchor)}
        onClose={handleCardMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {!isPmoHub && (
          <MenuItem onClick={handleCopyProjectId}>
            <ListItemIcon>
              <ContentCopy fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t("Copy Project ID")}</ListItemText>
          </MenuItem>
        )}
        {!isPmoHub && (
          <MenuItem
            onClick={() => {
              if (selectedProject?._id) {
                navigate(
                  `/defect-tracker/projects/${selectedProject._id}/applications`,
                );
              }
              handleCardMenuClose();
            }}
          >
            <ListItemIcon>
              <AppsOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t("Applications")}</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
